import { z } from 'zod';
/**
 * The vocabularies a scenario rule is written in.
 *
 * Primary and the staff app each declared their own copy of these, identical down to the member
 * values; they live here so a rule means one thing on both sides, and so the scenario upsert body
 * can eventually be published as a contract. `clients/fingerprint/scenarioRule.ts` and
 * `web/vivaa-api/src/models/scenario.ts` re-export them under the names they already used.
 */
/** How far back an apply-once window reaches. */
export var ScenarioApplyOnceUnit;
(function (ScenarioApplyOnceUnit) {
    ScenarioApplyOnceUnit["CalendarYear"] = "CALENDAR_YEAR";
    ScenarioApplyOnceUnit["Year"] = "YEAR";
    ScenarioApplyOnceUnit["CalendarMonth"] = "CALENDAR_MONTH";
    ScenarioApplyOnceUnit["Month"] = "MONTH";
    ScenarioApplyOnceUnit["CalendarWeek"] = "CALENDAR_WEEK";
    ScenarioApplyOnceUnit["LocalDay"] = "LOCAL_DAY";
    ScenarioApplyOnceUnit["Day"] = "DAY";
    ScenarioApplyOnceUnit["Hour"] = "HOUR";
})(ScenarioApplyOnceUnit || (ScenarioApplyOnceUnit = {}));
/** What the apply-once window is counted over. */
export var ScenarioApplyOnceEntity;
(function (ScenarioApplyOnceEntity) {
    ScenarioApplyOnceEntity["EhrLogin"] = "EHR_LOGIN";
    ScenarioApplyOnceEntity["Encounter"] = "ENCOUNTER";
    ScenarioApplyOnceEntity["Order"] = "ORDER";
    ScenarioApplyOnceEntity["PrevisitDocumentTemplate"] = "PREVISIT_DOCUMENT_TEMPLATE";
    ScenarioApplyOnceEntity["PrevisitFormTemplate"] = "PREVISIT_FORM_TEMPLATE";
    ScenarioApplyOnceEntity["Visit"] = "VISIT";
})(ScenarioApplyOnceEntity || (ScenarioApplyOnceEntity = {}));
/** Which point in that entity's life the window counts from. */
export var ScenarioApplyOnceStatus;
(function (ScenarioApplyOnceStatus) {
    ScenarioApplyOnceStatus["Completed"] = "COMPLETED";
    ScenarioApplyOnceStatus["Created"] = "CREATED";
    ScenarioApplyOnceStatus["Received"] = "RECEIVED";
    ScenarioApplyOnceStatus["Started"] = "STARTED";
    ScenarioApplyOnceStatus["Updated"] = "UPDATED";
})(ScenarioApplyOnceStatus || (ScenarioApplyOnceStatus = {}));
/** Whether the window is scoped to the practice that matched, any practice, or a named one. */
export var ScenarioApplyOncePracticeType;
(function (ScenarioApplyOncePracticeType) {
    ScenarioApplyOncePracticeType["Any"] = "ANY";
    ScenarioApplyOncePracticeType["Same"] = "SAME";
    ScenarioApplyOncePracticeType["Chosen"] = "CHOSEN";
})(ScenarioApplyOncePracticeType || (ScenarioApplyOncePracticeType = {}));
/** Something that happened to a patient, encounter or order that a rule can require. */
export var ScenarioEvent;
(function (ScenarioEvent) {
    ScenarioEvent["EncounterCreated"] = "ENCOUNTER_CREATED";
    ScenarioEvent["FormSetCreated"] = "FORM_SET_CREATED";
    ScenarioEvent["FormSetCompleted"] = "FORM_SET_COMPLETED";
    ScenarioEvent["FormSetStarted"] = "FORM_SET_STARTED";
    ScenarioEvent["FormSetExpired"] = "FORM_SET_EXPIRED";
    ScenarioEvent["NoticeOfAdmissionNew"] = "NOTICE_OF_ADMISSION_NEW";
    ScenarioEvent["NoticeOfAdmissionPending"] = "NOTICE_OF_ADMISSION_PENDING";
    ScenarioEvent["NoticeOfAdmissionApproved"] = "NOTICE_OF_ADMISSION_APPROVED";
    ScenarioEvent["NoticeOfAdmissionException"] = "NOTICE_OF_ADMISSION_EXCEPTION";
    ScenarioEvent["NoticeOfAdmissionDenied"] = "NOTICE_OF_ADMISSION_DENIED";
    ScenarioEvent["PatientCreated"] = "PATIENT_CREATED";
    ScenarioEvent["PatientCheckedIn"] = "PATIENT_CHECKED_IN";
    ScenarioEvent["PatientOrderNew"] = "PATIENT_ORDER_NEW";
    ScenarioEvent["PatientOrderProcessingSubmission"] = "PATIENT_ORDER_PROCESSING_SUBMISSION";
    ScenarioEvent["PatientOrderProcessedApproved"] = "PATIENT_ORDER_PROCESSED_APPROVED";
    ScenarioEvent["PatientOrderProcessedException"] = "PATIENT_ORDER_PROCESSED_EXCEPTION";
    ScenarioEvent["PatientOrderProcessedNotRequired"] = "PATIENT_ORDER_PROCESSED_NOT_REQUIRED";
    ScenarioEvent["PatientOrderProcessedPending"] = "PATIENT_ORDER_PROCESSED_PENDING";
    ScenarioEvent["PatientOrderProcessedRejected"] = "PATIENT_ORDER_PROCESSED_REJECTED";
    ScenarioEvent["PatientOrderApproved"] = "PATIENT_ORDER_APPROVED";
    ScenarioEvent["PatientOrderException"] = "PATIENT_ORDER_EXCEPTION";
    ScenarioEvent["PatientOrderPending"] = "PATIENT_ORDER_PENDING";
    ScenarioEvent["PatientOrderRejected"] = "PATIENT_ORDER_REJECTED";
    ScenarioEvent["PatientOrderNotRequired"] = "PATIENT_ORDER_NOT_REQUIRED";
    ScenarioEvent["PatientOrderNewPending"] = "PATIENT_ORDER_NEW_PENDING";
    ScenarioEvent["PatientOrderProcessingStatusing"] = "PATIENT_ORDER_PROCESSING_STATUSING";
    ScenarioEvent["PatientOrderStatusingPending"] = "PATIENT_ORDER_STATUSING_PENDING";
    ScenarioEvent["PatientOrderManualReviewRequired"] = "PATIENT_ORDER_MANUAL_REVIEW_REQUIRED";
    ScenarioEvent["PatientOrderUnknown"] = "PATIENT_ORDER_UNKNOWN";
    ScenarioEvent["PatientOrderMixedDetermination"] = "PATIENT_ORDER_MIXED_DETERMINATION";
    ScenarioEvent["PatientOrderProcessedMixedDetermination"] = "PATIENT_ORDER_PROCESSED_MIXED_DETERMINATION";
})(ScenarioEvent || (ScenarioEvent = {}));
export const ScenarioApplyOnceUnitSchema = z.enum(ScenarioApplyOnceUnit);
export const ScenarioApplyOnceEntitySchema = z.enum(ScenarioApplyOnceEntity);
export const ScenarioApplyOnceStatusSchema = z.enum(ScenarioApplyOnceStatus);
export const ScenarioApplyOncePracticeTypeSchema = z.enum(ScenarioApplyOncePracticeType);
export const ScenarioEventSchema = z.enum(ScenarioEvent);
//# sourceMappingURL=rule.js.map